import { useCallback, useEffect, useState } from 'react';
import { IconBackspace } from '../components/icons';

/*
 * Клавиатура пин-кода: четыре точки и цифровой блок. Один компонент служит
 * и вводу при входе, и установке (там его показывают дважды — ввод и повтор).
 * Код собирается локально и уходит наружу целиком, как только набраны 4 цифры.
 */

export function PinPad({
  title,
  hint,
  error,
  busy,
  onComplete,
  onCancel,
  cancelLabel,
}: {
  title: string;
  hint?: string;
  error?: string;
  busy?: boolean;
  onComplete: (pin: string) => void;
  onCancel?: () => void;
  cancelLabel?: string;
}) {
  const [pin, setPin] = useState('');

  // новая попытка после ошибки: точки очищаются
  useEffect(() => {
    if (error) setPin('');
  }, [error]);

  const push = useCallback(
    (d: string) => {
      if (busy) return;
      setPin((cur) => {
        if (cur.length >= 4) return cur;
        const next = cur + d;
        if (next.length === 4) setTimeout(() => onComplete(next), 80); // дать точке дорисоваться
        return next;
      });
    },
    [busy, onComplete],
  );

  const back = useCallback(() => setPin((cur) => cur.slice(0, -1)), []);

  // физическая клавиатура — на десктопе так быстрее
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) push(e.key);
      else if (e.key === 'Backspace') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [push, back]);

  return (
    <div className="pin">
      <h2 className="pin-title">{title}</h2>
      {hint && <div className="pin-hint">{hint}</div>}

      <div className="pin-dots" data-error={error ? 'true' : undefined}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`pin-dot${i < pin.length ? ' on' : ''}`} />
        ))}
      </div>

      <div className="pin-err">{error ? <span className="banner crit">{error}</span> : null}</div>

      <div className="pin-keys">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" className="pin-key" disabled={busy} onClick={() => push(d)}>
            {d}
          </button>
        ))}
        <span />
        <button type="button" className="pin-key" disabled={busy} onClick={() => push('0')}>
          0
        </button>
        <button
          type="button"
          className="pin-key ghost"
          disabled={busy || pin.length === 0}
          onClick={back}
          aria-label="Стереть"
        >
          <IconBackspace />
        </button>
      </div>

      {onCancel && (
        <button type="button" className="btn link pin-alt" onClick={onCancel}>
          {cancelLabel ?? 'Отмена'}
        </button>
      )}
    </div>
  );
}
